import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import AdminIcon from './AdminIcon';
import { useUser } from '../UserContext';

const AuditTrail = () => {
  const { permissions } = useUser();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const canView = !!permissions?.canViewAuditTrail;

  useEffect(() => {
    if (!canView) return undefined;
    // Fetch last 100 actions, ordered by most recent
    const q = query(
      collection(db, "systemLogs"), 
      orderBy("timestamp", "desc"), 
      limit(100)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLogs(logData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [canView]);

  const formatTimestamp = (ts) => {
    if (!ts) return "---";
    const date = ts.toDate();
    return date.toLocaleString('en-GB', { 
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' 
    });
  };

  const getActionClass = (type) => {
    if (type.includes('ACTIVATE')) return 'log-badge-success';
    if (type.includes('RECALL') || type.includes('DELETE')) return 'log-badge-danger';
    if (type.includes('REFINERY')) return 'log-badge-ai';
    if (type.includes('WORKFLOW')) return 'log-badge-workflow';
    if (type.includes('PRICING')) return 'log-badge-workflow';
    return 'log-badge-default';
  };

  if (!canView) return null;
  if (isLoading) return <div className="admin-placeholder">Accessing System Archives...</div>;

  return (
    <div className="audit-trail-view">
      <div className="registry-controls">
        <div className="stat-pill">System Integrity: SECURE</div>
        <div className="stat-pill">{logs.length} Recent Events Logged</div>
      </div>

      <div className="log-table">
        <div className="log-header">
          <div className="col-ts">Timestamp</div>
          <div className="col-user">Admin/System</div>
          <div className="col-type">Action Event</div>
          <div className="col-details">Parameters & Context</div>
        </div>

        {logs.map((log) => (
          <div key={log.id} className="log-row">
            <div className="col-ts">{formatTimestamp(log.timestamp)}</div>
            <div className="col-user">
              <span className="user-icon">👤</span> {log.adminName}
            </div>
            <div className="col-type">
              <span className={`log-badge ${getActionClass(log.actionType)}`}>
                {log.actionType.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="col-details">
              <div className="details-bubble">
                {log.details?.schoolName && <strong>{log.details.schoolName}</strong>}
                {log.details?.schoolId && <span className="id-tag">ID: {log.details.schoolId.slice(0,8)}...</span>}
                {log.details?.path && <span className="path-tag">Path: {log.details.path}</span>}
                {typeof log.details?.changeCount === 'number' && log.actionType === 'PRICING_UPDATE' && (
                  <span className="path-tag" style={{ display: 'block', marginTop: '6px' }}>
                    {log.details.changeCount} field change(s)
                  </span>
                )}
                {log.details?.summary && (
                  <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '0.78rem',
                    lineHeight: 1.45,
                    opacity: 0.92,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {log.details.summary}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}

        {logs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '100px', opacity: 0.3 }}>
            No system events recorded in current cycle.
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditTrail;